import React, { useState } from 'react'
import { Box, Flex, Text, Textarea } from '@chakra-ui/core'
import { Controller, useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { withAlpha } from 'utils'
import { EditIcon } from 'components/CommonComponents/EditIcon'
import keys from 'constants/keys'
import { Subtask } from 'components/TasksV2/TaskCreation/FormStepContext'
import { useWidgetErrorMessage } from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/useWidgetErrorMessage'
import useLocaleText from 'components/useLocaleText'
import { MessageSquare } from 'lucide-react'

interface IProps {
  subtaskPath: string
}

const defaultField: Subtask = {
  title: '',
  type: keys.SUBTASK_TYPES.OPEN_QUESTION,
  order_index: 0,
  is_required: true,
}

const AIOpenQuestionWidget: React.FC<IProps> = ({ subtaskPath }) => {
  const [isFocused, setIsFocused] = useState(false)

  const { control, errors } = useFormContext()
  const theme = useSelector((state: RootState) => state.general.theme)
  const questionError = useWidgetErrorMessage({ errors, index: 0, field: defaultField })
  const t_team_will_see = useLocaleText('ai_team_will_see_this')
  const t_placeholder = useLocaleText('ai_question_title_placeholder', true)

  const elementsColor = theme?.elementsColor || '#FF2D78'

  return (
    <Box px="10px" pb="16px" pt="8px">
      <Box
        borderRadius="14px"
        p="20px"
        style={{
          border: `1px solid ${withAlpha(elementsColor, 0.2)}`,
          background: withAlpha(elementsColor, 0.1),
        }}
      >
        {/* Label */}
        <Flex alignItems="center" justifyContent="center" mt="20px" mb="10px" style={{ gap: '6px' }}>
          <Text
            fontSize="14px"
            fontWeight="700"
            color={elementsColor}
            letterSpacing="1.5px"
            style={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}
          >
            {t_team_will_see}
          </Text>
        </Flex>
        {/* Question icon + editable title */}

        <Flex flexDir="column" alignItems="center" style={{ gap: '8px' }} borderRadius="10px" pos="relative">
          <Flex
            justifyContent="center"
            alignItems="center"
            bg="#EAD1DC"
            width="72px"
            height="72px"
            borderRadius="full"
            pos="absolute"
            top="25px"
            left="50%"
            transform="translateX(-50%)"
            zIndex={100}
          >
            <MessageSquare width={26} height={26} color="white" />
          </Flex>

          <Box w="100%" pt="30px" borderRadius="10px" mt="60px" bg="white" pos="relative">
            <Controller
              as={Textarea}
              name={`${subtaskPath}.title`}
              control={control}
              placeholder={t_placeholder}
              rules={{
                required: 'Title is required',
                validate: (value: string) => value.trim() !== '' || 'Title is required',
              }}
              key={`${subtaskPath}.title`}
              defaultValue=""
              resize="none"
              maxLength={100}
              border="none"
              bg="transparent"
              _focus={{ boxShadow: 'none', border: 'none' }}
              fontSize="14px"
              textAlign="center"
              style={{
                height: '76px',
                overflowY: 'auto',
                paddingTop: '24px',
                marginTop: '12px',
                paddingBottom: '12px',
                marginBottom: '16px',
                paddingRight: '16px',
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            {/* EditIcon fades out on focus, same behaviour as TaskInput */}
            <Box
              pos="absolute"
              bottom="50px"
              right="12px"
              pointerEvents="none"
              style={{ opacity: isFocused ? 0 : 1, transition: 'opacity 0.15s' }}
            >
              <EditIcon />
            </Box>
          </Box>

          {questionError && (
            <Text fontSize="12px" color="red.500" textAlign="center">
              {questionError}
            </Text>
          )}
        </Flex>
        {/* Hidden type controller */}
        <Controller
          name={`${subtaskPath}.type`}
          control={control}
          as={<Box />}
          key={`${subtaskPath}.type`}
          defaultValue={keys.SUBTASK_TYPES.OPEN_QUESTION}
        />
      </Box>
    </Box>
  )
}

export default AIOpenQuestionWidget
